import { InvestmentPlan } from './../../models/orders';
import { IRegistrationKiwiSaverEntityState } from './../../store/state/registration-kiwisaver.state';
import { catchError, filter, map } from 'rxjs/operators';
import { RegistrationKiwiSaverFacade } from './../../store/registration-kiwisaver/facade/kiwisaver-registration.facade';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { zip, Observable, of } from 'rxjs';
import { TokenService } from '../token/token.service';
import { UserRoleType } from "src/app/models/userRoleTypes";
import { AuthService } from "../http/auth.service";
import { GeneralSelectionFacade } from 'src/app/store/general-selection/facade/general-selection.facade';
import { AppConfig } from 'src/app/app.config';
import { SiteIds } from 'src/app/models/sites';
import { ClientEntity } from 'src/app/models/clients';
import { EntitySubType } from 'src/app/models/entityTypes';
import { deduplicatingSwitchMap } from 'src/app/utils/deduplicatingSwitchMap';
import { RegularInvestmentPlanFacade } from 'src/app/store/regular-investment-plan/regular-investment-plan.facade';
@Injectable()
export class KiwiSaverRedirectService {
    private kiwiSaverEntity$: Observable<IRegistrationKiwiSaverEntityState>;
    private investmentPlan$: Observable<Partial<InvestmentPlan>>;

    private isAdmin: boolean = false;
    private isAdvisor: boolean = false;

    constructor(
        private registrationKiwiSaverFacade: RegistrationKiwiSaverFacade,
        private tokenService: TokenService,
        private authService: AuthService,
        private generalSelectionFacade: GeneralSelectionFacade,
        private appConfig: AppConfig,
        private regularInvestmentPlanFacade: RegularInvestmentPlanFacade) {
        }


    public getRedirectRoute$(clientSelected: boolean = false) {

        const siteId = this.appConfig.appConfigSignal().env.siteId;

        this.isAdmin = this.tokenService.isAdministrator();
        this.isAdvisor = this.authService.doesUserHaveRole(UserRoleType.Advisor);

        if(this.isAdmin || this.isAdvisor){
            return this.getRedirectRouteForAdminAndAdvisors(clientSelected);
        }

        if(siteId === SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL){
            return this.getRedirectRouteForKiwiSaverPortal();
        } else if(siteId === SiteIds.INVESTNOWCOMBINED){
            return this.getRedirectRouteForHybridPortal();
        } else {
            return of('*');
        }
    }

    /**
     * Determines the correct route for admin and advisors in KiwiSaver portals by implementing the followng logic:
     * 1) if a client is selected the admin/advisor user can access their requested route.
     * 2) if not, and the user is an advisor, they should land on the overview page.
     * 3) if not, and the user is an admin they should land on the admin dashboard page.
     */
    private getRedirectRouteForAdminAndAdvisors(clientSelected: boolean) : Observable<string> {
        this.isAdmin = this.tokenService.isAdministrator();
        this.isAdvisor = this.authService.doesUserHaveRole(UserRoleType.Advisor);

        if ((this.isAdmin || this.isAdvisor)) {
            if (clientSelected) {
                return of('*');
            }
            else if(this.isAdvisor) {
                return of('/feature/overview');
            }
            else {
                return of('/feature/dashboard');
            }
        } 
    }

    /**
     * Determines the correct route for re-routing an investor in a KiwiSaver portal.
     * Incorporates specific logic (initially for InvestNow and now for other managers too) that supports the following:
     * 1) if the user does not have a KiwiSaver entity (but has a retail investor?) they will be taken to the register page.
     * 2) if the user has a KiwiSaver entity but no regular investment plan they must set one up so are taken to the RIP page.
     * 3) if the user has a KiwiSaver entity and a RIP they can access their requested route.
     * @returns an observable containing the redirect route.
     */
    private getRedirectRouteForKiwiSaverPortal() : Observable<string> {
        return this.getRedirectParamsForKiwiSaverPortal$().pipe(
            map((values: [IRegistrationKiwiSaverEntityState, InvestmentPlan]) => {
                const [kiwisaverEntity, investmentPlan] = values;
                const isEmptyInvestmentPlan = !investmentPlan  || (Object.keys(investmentPlan).length === 0 && investmentPlan.constructor === Object);
                if (!kiwisaverEntity.entityId && isEmptyInvestmentPlan) {
                    return '/register';
                } else if (kiwisaverEntity.entityId && isEmptyInvestmentPlan) {
                    return '/feature/client/buy-sell/regular-investment-plan';
                } else {
                    return '*';
                }
            })
        );
    }

    /**
     * Retrieves the data required to determine the redirect route for KiwiSaver portals.
     * @returns a zipped observable of a users KiwiSaver registration status and the KiwiSaver investors regular investment plan if it exists.
     */
    private getRedirectParamsForKiwiSaverPortal$() : Observable<[IRegistrationKiwiSaverEntityState, Partial<InvestmentPlan>]> {
        this.kiwiSaverEntity$ = this.registrationKiwiSaverFacade.getKiwisaverEntity().pipe(
            filter((entity) => entity != null),
        );
        this.investmentPlan$ = this.regularInvestmentPlanFacade.getRegularInvestmentPlan().pipe(
            filter((ip) => ip != null),
            map((ip) => {
                return ip;
            })
        );

        return zip(
            this.kiwiSaverEntity$,
            this.investmentPlan$
        );
    }

    /**
     * Determines the correct route for re-routing an investor in a portal where both KiwiSaver and standard investors exist.
     * Incorporates specific logic for InvestNow that supports the following:
     * 1) if the user is attempting to view a retail investor they can access the requested route.
     * 2) if the user has a KiwiSaver entity selected and the entity does not have a regular investment plan they must set one up so are taken to the RIP page.
     * 3) if the user has a KiwiSaver entity selected and the entity has a regular investment plan they can access the requested route.
     */
    private getRedirectRouteForHybridPortal() : Observable<string> {
        return this.getRedirectParamsForHybridPortal$()
        .pipe(
            map((values: [ClientEntity, boolean]) => {

                const [selectedEntity, hasRip] = values;
                const isEmptyInvestmentPlan = hasRip;

                if(!selectedEntity)
                    return '*';

                if(selectedEntity.EntitySubType === EntitySubType.Investor){
                    return '*';
                } else if (selectedEntity.EntityId && !isEmptyInvestmentPlan) {
                    return '/feature/client/buy-sell/regular-investment-plan';
                } else {
                    return '*';
                }
            })
        );
    }

    /**
     * A local signal to store the current regular investment plan state.
     * This is used to avoid unnecessary API calls when the regular investment plan already belongs to the selected portfolio.
     */
    private redirectStatus: WritableSignal<IRedirectState> = signal(undefined);

    /**
     * Retrieves the data required to determine the redirect route for the a hybrid portal.
     * @returns a zipped observable of the selected entity and the investors regular investment plan if it exists.
     */
    private getRedirectParamsForHybridPortal$(): Observable<[ClientEntity, boolean]> {

        let state$ = this.generalSelectionFacade.getSelectedEntityPortfolioState$
        .pipe(
            map((state) => {
                return state;
            })
        )

        return state$.pipe(
            deduplicatingSwitchMap((selectionState) => {
                let portfolioId = selectionState.selectedPortfolios[0]?.id;

                //If we dont have a portfolio selected then we cannot determine the route.
                if(portfolioId === undefined || portfolioId === null) {
                    return of([null, false]);
                }

                //If the current regular investment plan status is for the selected portfolio and they already have
                //a regular investment plan we can just return.
                let redirectStatus = this.redirectStatus();
                if(redirectStatus?.portfolioId === portfolioId && redirectStatus.hasRegularInvestmentPlan) {
                    return of([selectionState.selectedEntities[0], redirectStatus.hasRegularInvestmentPlan]);
                }

                //If this is a non-KiwiSaver investor then we can just return because the regular investment plan is not relevant to determine the route.
                //This will help to reduce API calls.
                if(selectionState.selectedEntities[0].EntitySubType !== EntitySubType.KiwiSaverInvestor) {
                    return of([selectionState.selectedEntities[0], true]);
                }

                return this.regularInvestmentPlanFacade.getInvestmentPlan(portfolioId)
                .pipe(
                    catchError(() => of(null)),
                    map((investmentPlan) => {

                        let hasRegularInvestmentPlan = investmentPlan !== null && Object.keys(investmentPlan).length > 0;

                        this.redirectStatus.set({
                            portfolioId: portfolioId,
                            hasRegularInvestmentPlan: hasRegularInvestmentPlan
                        });

                        return [selectionState.selectedEntities[0], hasRegularInvestmentPlan];
                    })
                );
            })
        );
    }
}

export interface IRedirectState {
    portfolioId: number;
    hasRegularInvestmentPlan?: boolean;
}